// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Breadcrumbs.tsx
 * @input Uses React, createContext, stylex, theme tokens
 * @output Exports Breadcrumbs component, BreadcrumbsProps, BreadcrumbContext
 * @position Core container component; consumed by index.ts
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Breadcrumbs/Breadcrumbs.doc.mjs
 * - /packages/core/src/Breadcrumbs/Breadcrumbs.test.tsx
 * - /packages/core/src/Breadcrumbs/index.ts
 * - /apps/storybook/stories/Breadcrumbs.stories.tsx
 * - /packages/cli/templates/blocks/components/Breadcrumbs/ (showcase blocks)
 */
import { createContext, useMemo } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { spacingVars } from "../theme/tokens.stylex.js";
import { mergeProps } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";

// =============================================================================
// Variant type
// =============================================================================

/**
 * Extensible variant map for Breadcrumbs.
 *
 * Theme packages can add custom variants via TypeScript module augmentation:
 * @example
 * ```
 * declare module '@astryxdesign/core/Breadcrumbs' {
 *   interface BreadcrumbsVariantMap {
 *     'compact': true;
 *   }
 * }
 * ```
 */

/**
 * Visual variant for the breadcrumb trail.
 * - `'default'`: Standard text styling
 * - `'supporting'`: Smaller, secondary text for supporting context
 *
 * Extensible via module augmentation of BreadcrumbsVariantMap.
 */

// =============================================================================
// Context shared with BreadcrumbItem
// =============================================================================

/** @internal Context for passing variant and separator from Breadcrumbs to BreadcrumbItem. */
import { jsx as _jsx } from "react/jsx-runtime";
export const BreadcrumbContext = /*#__PURE__*/createContext({
  variant: 'default',
  separator: '/'
});
BreadcrumbContext.displayName = 'BreadcrumbContext';

// =============================================================================
// Props
// =============================================================================

// =============================================================================
// Styles
// =============================================================================

const navStyles = {
  root: {
    k1xSpc: "x1lliihq",
    $$css: true
  }
};
// =============================================================================
// Component
// =============================================================================

/**
 * A navigation breadcrumb trail. Wraps BreadcrumbItem children in
 * semantic `<nav>` + `<ol>` markup with separators between items.
 *
 * Auto-detects the last child as the current page if no item has
 * `isCurrent` explicitly set — handled by each item via DOM inspection,
 * no React child introspection needed.
 *
 * @example
 * ```
 * <Breadcrumbs>
 *   <BreadcrumbItem href="/">Home</BreadcrumbItem>
 *   <BreadcrumbItem href="/projects">Projects</BreadcrumbItem>
 *   <BreadcrumbItem isCurrent>My Project</BreadcrumbItem>
 * </Breadcrumbs>
 * ```
 */
export function Breadcrumbs({
  children,
  separator = '/',
  variant = 'default',
  xstyle,
  className,
  style,
  label: labelFromProps,
  ref,
  ...rest
}) {
  const t = useTranslator();
  const label = labelFromProps ?? t('@astryx.breadcrumbs.label');
  const ctxValue = useMemo(() => ({
    variant,
    separator
  }), [variant, separator]);
  return /*#__PURE__*/_jsx(BreadcrumbContext, {
    value: ctxValue,
    children: /*#__PURE__*/_jsx("nav", {
      ref: ref,
      "aria-label": label,
      ...mergeProps(themeProps('breadcrumbs', {
        variant
      }), stylex.props(navStyles.root, xstyle), className, style),
      ...rest,
      children: /*#__PURE__*/_jsx("ol", {
        ...{
          className: "x78zum5 x6s0dn4 x1a02dak xe8uvvx x1ghz6dp x1717udv xzye2dw"
        },
        children: children
      })
    })
  });
}
Breadcrumbs.displayName = 'Breadcrumbs';